import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { UserService } from 'src/app/core/common-services/user.services';
import { AdminProfileCompletionService } from 'src/app/core/common-services/admin-profile-completion.service';
import { LanguageService } from 'src/app/shared/services/language.service';
import {
  NotificationService,
} from './../../core/common-services/notification.service';
import { LoadingSpinnerShellService } from 'src/app/core/common-services/loading-spinner-shell.service';
import { GlobalLoadingService } from 'src/app/core/common-services/global-loading.service';
import { filter, Subscription } from 'rxjs';
import { NotificationMsg } from 'src/app/core/data-models/notification.data.models';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements AfterViewInit, OnInit {
  /**
   * Custom HTML for ngx-spinner (logo + hint; styled in styles.css).
   * Built after i18n is ready so GLOBAL_LOADING_HINT translates correctly.
   */
  talLoadingSpinnerTemplate = '';

  private loadingHintLangSub?: Subscription;

  title = 'Tal-Portal';
  currentSystemLanguage: string = 'ar';
  style: boolean = false;
  notifications: NotificationMsg[] = [];
  showLayout = !AppComponent.isAuthRouteFromLocation();
  sidebarCollapsed = false;
  sidebarCollapsedMobile = true;

  isRtl = false;
  isMobile: boolean = false;

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;
  }

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }
  toggleSidebarMobile() {
    this.sidebarCollapsedMobile = !this.sidebarCollapsedMobile;
  }

  toggleLang() {
    this.isRtl = !this.isRtl;
  }
  constructor(
    public router: Router,
    public notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private adminProfileService: AdminProfileCompletionService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private _elementRef: ElementRef,
    private readonly loadingSpinnerShell: LoadingSpinnerShellService,
    private readonly globalLoading: GlobalLoadingService
  ) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.updateShowLayoutFromUrl(event.urlAfterRedirects || event.url || '');
        this.cdr.detectChanges();
      });
    // Configure available languages
    this.translate.addLangs(['en', 'ar']);
    this.translate.setDefaultLang('ar');

    const browserLang = this.translate.getBrowserLang() || 'ar'; // fallback if undefined
    this.currentSystemLanguage = browserLang.match(/en|ar/)
      ? browserLang
      : 'ar';

    // Initialize RTL based on language service
    this.isRtl = this.languageService.currentLanguage === 'ar';
  }
  // onSidebarStateChange(state: boolean) {
  //   this.sidebarCollapsed = state;
  // }
  logout() {
    this.userService.logout();
  }

  ngOnInit(): void {
    this.notifications = this.notificationService.notifications;

    const initialUrl = this.getInitialUrl();
    this.updateShowLayoutFromUrl(initialUrl);

    if (this.isAuthRoute(initialUrl)) {
      this.userService.clearStaleSession();
    }

    // Update RTL status based on language service
    const savedLang = localStorage.getItem('currentLang') || 'en';
    this.languageService.setLanguage(savedLang);
    this._elementRef.nativeElement.removeAttribute('ng-version');

    this.isRtl = this.languageService.currentLanguage === 'ar';

    if (this.userService.isLoggedIn()) {
      // this.signalrService.startConnection(this.userService.currentUserCredantials.currentUsedInvestorAccountId);
    }
    this.checkScreenSize();

    this.refreshTalLoadingSpinnerTemplate();
    this.loadingHintLangSub = this.translate.onLangChange.subscribe(() =>
      this.refreshTalLoadingSpinnerTemplate()
    );
  }

  private refreshTalLoadingSpinnerTemplate(): void {
    this.talLoadingSpinnerTemplate = this.buildTalLoadingSpinnerTemplate();
  }

  private buildTalLoadingSpinnerTemplate(): string {
    const hint = this.escapeHtml(this.translate.instant('GLOBAL_LOADING_HINT'));
    return `
<div class="tal-loading-spinner-inner">
  <img
    class="tal-loading-spinner-logo"
    src="assets/image/logo.png"
    alt=""
    width="240"
  />
  <p class="tal-loading-spinner-hint" aria-live="polite">${hint}</p>
</div>`;
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private getInitialUrl(): string {
    const url = this.router.url;
    if (url && url !== '/' && url !== '') return url;
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (hash && hash.startsWith('#')) return hash.slice(1).split('?')[0];
    return url || '';
  }

  /** Sidebar + top nav around the single always-mounted router-outlet. */
  get showAppChrome(): boolean {
    return this.isLoggedIn() && this.showLayout;
  }

  private updateShowLayoutFromUrl(url: string): void {
    this.showLayout = !this.isAuthRoute(url);
    this.syncLoadingSpinnerShell();
  }

  private isAuthRoute(url: string): boolean {
    const u = (url || '').toLowerCase();
    return u.includes('/authentication') || u.includes('/auth');
  }

  private static isAuthRouteFromLocation(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }
    const hash = window.location.hash || '';
    const path = hash.startsWith('#') ? hash.slice(1).split('?')[0] : window.location.pathname;
    const u = path.toLowerCase();
    return u.includes('/authentication') || u.includes('/auth');
  }

  /** Guest vs main spinner must match which spinner DOM is mounted (see app.component.html). */
  private syncLoadingSpinnerShell(): void {
    this.loadingSpinnerShell.activeName = this.showAppChrome ? 'main' : 'guest';
    this.globalLoading.reconcileShell();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.loadingHintLangSub?.unsubscribe();
    localStorage.removeItem('userNameEn');
    this.router.navigate(['/authentication/login']);
  }

  /** Send a test message and error to Sentry (for verification). Only when DSN is set. */
  sendSentryTest() {
    if (!environment.sentry?.dsn) {
      console.warn('[Sentry] No DSN configured. Set environment.sentry.dsn to test.');
      return;
    }
    Sentry.captureMessage('TAL-Dash manual test (log)', 'info');
    Sentry.captureException(new Error('TAL-Dash manual test (error)'));
  }

  get sentryTestEnabled(): boolean {
    return !!environment.sentry?.dsn && !environment.production;
  }

  isHome() {
    return !this.router.url.includes('/home');
  }

  isLoggedIn() {
    if (this.isAuthRoute(this.router.url || this.getInitialUrl())) {
      return false;
    }
    return this.userService.isLoggedIn();
  }
  // isLoggedIn() {
  //   return (
  //     this.userService.currentUserCredantials != undefined &&
  //     this.userService.isLoggedIn()
  //   );
  // }

  removeNotification(notification: NotificationMsg) {
    this.notificationService.removeNotification(notification);
  }

  // Switch language at runtime
  switchLanguage(lang: 'en' | 'ar') {
    this.currentSystemLanguage = lang;
    this.translate.use(lang);
    this.languageService.setLanguage(lang);
    this.isRtl = lang === 'ar';
    localStorage.setItem('lang', lang);
  }


  // close() {
  //   document.getElementById('errorMsg')!.innerHTML = '';
  //   if (document.getElementsByClassName('error_toaster_show').length > 0)
  //     document
  //       .getElementsByClassName('error_toaster_show')[0]
  //       .setAttribute('class', 'error_toaster');

  //   document.getElementById('successMsg')!.innerHTML = '';
  //   if (document.getElementsByClassName('success_toaster_show').length > 0)
  //     document
  //       .getElementsByClassName('success_toaster_show')[0]
  //       .setAttribute('class', 'success_toaster');

  //   document.getElementById('warningMsg')!.innerHTML = '';
  //   if (document.getElementsByClassName('warning_toaster_show').length > 0)
  //     document
  //       .getElementsByClassName('warning_toaster_show')[0]
  //       .setAttribute('class', 'warning_toaster');

  //   document.getElementById('infoMsg')!.innerHTML = '';
  //   if (document.getElementsByClassName('info_toaster_show').length > 0)
  //     document
  //       .getElementsByClassName('info_toaster_show')[0]
  //       .setAttribute('class', 'info_toaster');
  // }
}
